import { onBeforeUnmount, readonly, ref, shallowRef, unref, watch } from 'vue'
import {
  createMeetingReplayTask,
  getLatestMeetingReplayTask,
  getMeetingReplayTask,
  getRecoverableMeetingReplayCaptures,
  recoverMeetingReplayCapture,
  retryMeetingReplayOutput,
  retryMeetingReplayTask,
} from '@/api/meetingReplay'

const POLL_INTERVAL = 3000
const DISCOVERY_INTERVAL = 10000
const SUCCESS_CODES = [0, 200, '0', '200']

export function shouldPollReplayTask(task) {
  if (!task) return false
  if (task.status === 'PROCESSING') return true
  if (task.status !== 'PENDING') return false
  return (
    ['UNKNOWN', 'WAITING'].includes(task.eligibilityStatus) ||
    ['UNRESOLVED', 'REQUESTED'].includes(task.generationRequestState)
  )
}

function idempotencyKey() {
  if (typeof window !== 'undefined' && window.crypto && typeof window.crypto.randomUUID === 'function') {
    return window.crypto.randomUUID()
  }
  return `replay-${Date.now()}-${Math.random().toString(16).slice(2)}`
}

function responseData(response, { allowNull = false } = {}) {
  if (!response || typeof response !== 'object' || !SUCCESS_CODES.includes(response.code)) {
    throw new Error('MEETING_REPLAY_RESPONSE_INVALID')
  }
  const data = Object.prototype.hasOwnProperty.call(response, 'data') ? response.data : null
  if (data == null && !allowNull) throw new Error('MEETING_REPLAY_RESPONSE_DATA_REQUIRED')
  return data
}

export function useMeetingReplayTask(meetingCode) {
  const task = ref(null)
  const recoverableCaptures = ref([])
  const loading = shallowRef(false)
  const actionLoading = shallowRef(false)
  const networkError = shallowRef(false)
  let pollTimer = null
  let scopeVersion = 0
  let requestSequence = 0

  const stopPolling = () => {
    if (pollTimer) window.clearTimeout(pollTimer)
    pollTimer = null
  }

  const beginRequest = () => ({
    code: String(unref(meetingCode) || ''),
    requestId: ++requestSequence,
    scope: scopeVersion,
  })

  const isCurrentRequest = (context) =>
    context.scope === scopeVersion &&
    context.requestId === requestSequence &&
    context.code === String(unref(meetingCode) || '')

  const schedulePolling = () => {
    stopPolling()
    const hasTask = Boolean(task.value?.taskId)
    if (hasTask && !shouldPollReplayTask(task.value)) return
    pollTimer = window.setTimeout(
      () => {
        pollTask().catch(() => {})
      },
      hasTask ? POLL_INTERVAL : DISCOVERY_INTERVAL
    )
  }

  const applyTask = (nextTask, context, nextRecoverableCaptures = []) => {
    if (!isCurrentRequest(context)) return null
    task.value = nextTask
    recoverableCaptures.value = nextTask ? [] : nextRecoverableCaptures
    networkError.value = false
    schedulePolling()
    return task.value
  }

  const handleReadFailure = (error, context) => {
    if (isCurrentRequest(context)) {
      networkError.value = true
      schedulePolling()
    }
    throw error
  }

  const discoverLatest = async ({ silent = false, includeRecoverable = true } = {}) => {
    const code = String(unref(meetingCode) || '').trim()
    if (!code) {
      task.value = null
      stopPolling()
      return null
    }
    const context = beginRequest()
    if (!silent) loading.value = true
    stopPolling()
    try {
      const response = await getLatestMeetingReplayTask(code)
      const nextTask = responseData(response, { allowNull: true })
      let nextRecoverableCaptures = recoverableCaptures.value
      if (!nextTask && includeRecoverable) {
        try {
          const recoverableResponse = await getRecoverableMeetingReplayCaptures(code)
          const recoverableData = responseData(recoverableResponse, { allowNull: true })
          nextRecoverableCaptures = Array.isArray(recoverableData) ? recoverableData : []
        } catch (recoveryDiscoveryError) {
          // 支持前后端滚动发布：后端恢复接口尚未上线时，仍保留原有任务查询页面。
          nextRecoverableCaptures = []
        }
      }
      return applyTask(nextTask, context, nextRecoverableCaptures)
    } catch (error) {
      return handleReadFailure(error, context)
    } finally {
      if (isCurrentRequest(context)) loading.value = false
    }
  }

  async function pollTask() {
    const taskId = task.value?.taskId
    // 空任务轮询只查轻量 latest；可恢复 capture 在首次进入或用户刷新时查询，
    // 避免对长会议字幕表每 10 秒做一次重复扫描。
    if (!taskId) return discoverLatest({ silent: true, includeRecoverable: false })
    const context = beginRequest()
    stopPolling()
    try {
      const response = await getMeetingReplayTask(taskId)
      const nextTask = responseData(response)
      if (String(nextTask.taskId) !== String(taskId)) throw new Error('MEETING_REPLAY_TASK_MISMATCH')
      return applyTask(nextTask, context)
    } catch (error) {
      return handleReadFailure(error, context)
    }
  }

  const runAction = async (action, validate) => {
    if (actionLoading.value) throw new Error('MEETING_REPLAY_ACTION_IN_PROGRESS')
    const context = beginRequest()
    actionLoading.value = true
    stopPolling()
    try {
      const response = await action()
      const nextTask = responseData(response)
      if (validate && !validate(nextTask)) throw new Error('MEETING_REPLAY_ACTION_RESPONSE_MISMATCH')
      return applyTask(nextTask, context)
    } finally {
      if (isCurrentRequest(context)) {
        actionLoading.value = false
        schedulePolling()
      }
    }
  }

  const createTask = () => {
    const captureSessionId = task.value?.captureSessionId
    if (!captureSessionId) return Promise.reject(new Error('MEETING_REPLAY_CAPTURE_REQUIRED'))
    return runAction(
      () => createMeetingReplayTask(captureSessionId, idempotencyKey()),
      (nextTask) => nextTask.captureSessionId === captureSessionId
    )
  }

  const recoverCapture = (captureSessionId) => {
    const code = String(unref(meetingCode) || '').trim()
    if (!code || !captureSessionId) return Promise.reject(new Error('MEETING_REPLAY_CAPTURE_REQUIRED'))
    return runAction(
      () => recoverMeetingReplayCapture(code, captureSessionId, idempotencyKey()),
      (nextTask) => nextTask.meetingCode === code && nextTask.captureSessionId === captureSessionId
    )
  }

  const retryTask = () => {
    const taskId = task.value?.taskId
    if (!taskId) return Promise.reject(new Error('MEETING_REPLAY_TASK_REQUIRED'))
    return runAction(
      () => retryMeetingReplayTask(taskId, idempotencyKey()),
      (nextTask) => String(nextTask.taskId) === String(taskId)
    )
  }

  const retryOutput = (outputId) => {
    const taskId = task.value?.taskId
    if (!taskId || !outputId) return Promise.reject(new Error('MEETING_REPLAY_OUTPUT_REQUIRED'))
    return runAction(
      () => retryMeetingReplayOutput(taskId, outputId, idempotencyKey()),
      (nextTask) => String(nextTask.taskId) === String(taskId)
    )
  }

  watch(
    () => String(unref(meetingCode) || '').trim(),
    () => {
      scopeVersion += 1
      requestSequence += 1
      task.value = null
      recoverableCaptures.value = []
      loading.value = false
      actionLoading.value = false
      networkError.value = false
      stopPolling()
      discoverLatest().catch(() => {})
    },
    { immediate: true }
  )

  onBeforeUnmount(() => {
    scopeVersion += 1
    requestSequence += 1
    stopPolling()
  })

  return {
    task: readonly(task),
    recoverableCaptures: readonly(recoverableCaptures),
    loading: readonly(loading),
    actionLoading: readonly(actionLoading),
    networkError: readonly(networkError),
    refresh: discoverLatest,
    createTask,
    recoverCapture,
    retryTask,
    retryOutput,
  }
}
